<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { Chart } from 'chart.js/auto'
import DemoAllocationDisclaimer from '@/components/landing/DemoAllocationDisclaimer.vue'

const props = defineProps({
  allocationData: {
    type: Object,
    required: true
  },
  isDemoMode: {
    type: Boolean,
    default: false
  },
  onReassess: {
    type: Function,
    default: null
  },
  onBackToLanding: {
    type: Function,
    default: null
  }
})

console.log('AssetAllocationResult - allocationData:', props.allocationData)
console.log('AssetAllocationResult - isDemoMode:', props.isDemoMode)

// Extract allocation data
const allocation = computed(() => {
  if (props.isDemoMode && props.allocationData?.assetAllocation) {
    // Demo mode - use assetAllocation object
    return props.allocationData.assetAllocation
  } else if (props.allocationData?.asset_allocation) {
    // Real API mode - use asset_allocation
    return props.allocationData.asset_allocation
  }
  return {}
})

const riskProfile = computed(() => {
  return props.allocationData?.riskProfile || props.allocationData?.risk_profile || 'Unknown'
})

console.log('Computed allocation:', allocation.value)
console.log('Risk profile:', riskProfile.value)

// Format percentage for display
const formatPercentage = (value) => `${value}%`;

// Safe getRiskColor function with null checks
const getRiskColor = (profile) => {
  if (!profile) return '#6c757d' // Default gray color

  const profileLower = String(profile).toLowerCase()

  const colors = {
    conservative: '#28a745',
    moderate: '#ffc107',
    aggressive: '#dc3545'
  }
  return colors[profileLower] || '#6c757d'
}

// Safe helper to get investment horizon
const getInvestmentHorizon = () => {
  if (props.isDemoMode) {
    return 'Long-Term' // Default for demo
  }
  return props.allocationData?.investment_horizon || props.allocationData?.investmentHorizon || 'N/A'
}

// Safe helper to get expected return
const getExpectedReturn = () => {
  if (props.isDemoMode && props.allocationData?.expectedReturn) {
    return props.allocationData.expectedReturn
  }
  return props.allocationData?.expected_return || props.allocationData?.expectedReturn || 'N/A'
}

// Safe helper to get client name
const getClientName = () => {
  if (props.isDemoMode) {
    return 'Client' // Generic for demo mode
  }
  return props.allocationData?.client_name || props.allocationData?.name || 'Client'
}

// Safe helper to get email
const getEmail = () => {
  if (props.isDemoMode) {
    return 'N/A' // Hide email in demo mode
  }
  return props.allocationData?.email || 'N/A'
}

// Safe helper to get age
const getAge = () => {
  if (props.isDemoMode) {
    return 'N/A' // Hide age in demo mode
  }
  return props.allocationData?.age || 'N/A'
}

// Get expected return display
const getExpectedReturnDisplay = computed(() => {
  if (props.allocationData.expectedReturn) {
    return props.allocationData.expectedReturn;
  }
  return "10-12% p.a.";
});

// Define processing functions BEFORE watch
const processDummyAllocation = (data) => {
  // Transform dummy data into chart format
  if (data?.assetAllocation) {
    const labels = Object.keys(data.assetAllocation)
    const values = Object.values(data.assetAllocation)

    console.log('Dummy allocation labels:', labels)
    console.log('Dummy allocation values:', values)

    // Set up chart data...
  }
}

const processRealAllocation = (data) => {
  // Handle real API response
  // ...existing logic...
}

// Watch for allocationData changes
watch(() => props.allocationData, (newData) => {
  console.log('AssetAllocationResult received data:', newData)
  console.log('isDemoMode:', props.isDemoMode)

  if (newData?.isDemoMode) {
    console.log('Processing dummy allocation data')
    // Process dummy allocation for display
    processDummyAllocation(newData)
  } else {
    console.log('Processing real allocation data')
    // Process real API allocation
    processRealAllocation(newData)
  }
}, { immediate: true })

// Chart rendering
const allocationChart = ref(null)
let chartInstance = null

const renderChart = () => {
  if (!allocationChart.value || !allocation.value || Object.keys(allocation.value).length === 0) {
    console.log('Cannot render chart - missing data or canvas')
    return
  }

  // Destroy existing chart if it exists
  if (chartInstance) {
    chartInstance.destroy()
  }

  const ctx = allocationChart.value.getContext('2d')
  const labels = Object.keys(allocation.value)
  const data = Object.values(allocation.value)

  console.log('Rendering chart with labels:', labels, 'data:', data)

  // Generate colors for chart
  const backgroundColors = [
    '#4CAF50', '#2196F3', '#FFC107', '#FF5722', '#9C27B0',
    '#00BCD4', '#FF9800', '#795548', '#607D8B', '#E91E63'
  ]

  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: backgroundColors.slice(0, labels.length),
        borderWidth: 2,
        borderColor: '#ffffff'
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            padding: 15,
            font: {
              size: 12,
              family: 'Satoshi-Variable, sans-serif'
            }
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || ''
              const value = context.parsed || 0
              return `${label}: ${value}%`
            }
          }
        }
      }
    }
  })
}

// Render chart when component mounts or data changes
onMounted(() => {
  console.log('AssetAllocationResult mounted')
  nextTick(() => {
    renderChart()
  })
})

watch([allocation, allocationChart], () => {
  console.log('Allocation or canvas changed, re-rendering chart')
  nextTick(() => {
    renderChart()
  })
})
</script>

<template>
  <div class="asset-allocation-result">
    <!-- Demo Mode Disclaimer Banner -->
    <DemoAllocationDisclaimer
      v-if="isDemoMode"
      :disclaimer="allocationData?.disclaimer"
    />

    <!-- Main allocation display -->
    <div class="allocation-content">
      <div class="allocation-header">
        <h2 class="allocation-title">Recommended Asset Allocation</h2>
        <p class="allocation-subtitle">
          Scientifically optimized using advanced algorithms, market data, and proven portfolio theory.
        </p>
      </div>

      <!-- Client Info Cards -->
      <div class="info-cards-grid">
        <div class="info-card">
          <span class="info-label">CLIENT NAME:</span>
          <span class="info-value">{{ getClientName() }}</span>
        </div>
        <div class="info-card">
          <span class="info-label">EMAIL:</span>
          <span class="info-value">{{ getEmail() }}</span>
        </div>
        <div class="info-card">
          <span class="info-label">AGE:</span>
          <span class="info-value">{{ getAge() }}</span>
        </div>
      </div>

      <div class="info-cards-grid">
        <div class="info-card">
          <span class="info-label">RISK PROFILE:</span>
          <span class="info-value risk-badge" :style="{ backgroundColor: getRiskColor(riskProfile) }">
            {{ riskProfile }}
          </span>
        </div>
        <div class="info-card">
          <span class="info-label">INVESTMENT HORIZON:</span>
          <span class="info-value">{{ getInvestmentHorizon() }}</span>
        </div>
        <div class="info-card">
          <span class="info-label">EXPECTED RETURN:</span>
          <span class="info-value expected-return">{{ getExpectedReturn() }}</span>
        </div>
      </div>

      <!-- Asset Allocation Chart -->
      <div v-if="allocation && Object.keys(allocation).length > 0" class="allocation-chart-section">
        <h3 class="section-title">Asset Allocation Breakdown</h3>
        <div class="chart-container">
          <canvas ref="allocationChart"></canvas>
        </div>

        <!-- Allocation Table -->
        <div class="allocation-table">
          <div
            v-for="(value, key) in allocation"
            :key="key"
            class="allocation-row"
          >
            <span class="asset-name">{{ key }}</span>
            <span class="asset-percentage">{{ value }}%</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button v-if="onReassess" @click="onReassess" class="reassess-button">
          Reassess
        </button>
        <button v-if="onBackToLanding" @click="onBackToLanding" class="back-home-button">
          Back to Home
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.asset-allocation-result {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  font-family: "Satoshi-Variable", sans-serif;
}

.allocation-content {
  padding: 24px;
  background: var(--an-white);
  border-radius: 12px;
  box-shadow: var(--an-card-shadow);
  border: 1px solid var(--an-gray3);
}

.allocation-header {
  text-align: center;
  margin-bottom: 32px;
}

.allocation-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 8px;
  background: linear-gradient(135deg, var(--an-primary) 0%, var(--an-secondary) 50%, var(--an-tertiary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.allocation-subtitle {
  font-size: 1rem;
  font-weight: 400;
  color: var(--an-text-secondary);
  margin: 0;
}

/* Client Info Cards */
.info-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;
}

.info-card {
  background: var(--an-white2);
  border: 1px solid var(--an-gray3);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  box-shadow: var(--an-card-shadow);
}

.info-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.info-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--an-text-primary);
}

/* Risk Profile Badge */
.risk-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  color: var(--an-white);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

/* Asset Allocation Chart */
.allocation-chart-section {
  margin-bottom: 32px;
}

.chart-container {
  position: relative;
  width: 100%;
  height: 400px;
  margin-bottom: 16px;
}

/* Allocation Table */
.allocation-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.allocation-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--an-gray3);
}

.asset-name {
  font-size: 1rem;
  font-weight: 500;
  color: var(--an-text-primary);
}

.asset-percentage {
  font-size: 1rem;
  font-weight: 700;
  color: var(--an-primary);
  text-align: right;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
}

.reassess-button, .back-home-button {
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  min-width: 120px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.reassess-button {
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
  border: none;
  box-shadow: 0 4px 12px rgba(0, 78, 80, 0.25);
}

.reassess-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 78, 80, 0.3);
}

.back-home-button {
  background: var(--an-white);
  border: 2px solid var(--an-primary);
  color: var(--an-primary);
}

.back-home-button:hover {
  background: var(--an-primary);
  color: var(--an-white);
  transform: translateY(-1px);
}

/* Demo Disclaimer */
.demo-disclaimer {
  margin-top: 32px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  border: 1px solid var(--an-gray3);
  text-align: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .asset-allocation-result {
    padding: 16px;
  }

  .allocation-title {
    font-size: 1.8rem;
  }

  .info-cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .allocation-chart-section {
    height: 300px;
  }

  .action-buttons {
    flex-direction: column;
    align-items: center;
  }
}

@media (max-width: 480px) {
  .info-card {
    padding: 12px;
  }

  .allocation-percentage {
    font-size: 1.5rem;
  }

  .asset-title {
    font-size: 1rem;
  }
}
</style>
